/**
 * RewardStrip — the co-visible payoff (direction §2/§5).
 *
 * Three groups, divided by hairline rules, on a dashed top border:
 *   1. Streak  — 🔥 + Instrument-Serif count + "day streak"
 *   2. Days-done ring (W3) — <DaysDoneRing>, advances on every completion
 *   3. On-track — "✓ {done} mi this week · on track" (the athlete's own
 *      unit; B330: only when something is done and the week is on plan, else
 *      no line at all)
 *
 * Sits inside the loop hero, right above the CTA, so the reward is
 * co-visible with the action and animates on completion (PR 2 drives the
 * beat via `animateOnMount`). Streak source is the caller's
 * `computeCurrentStreak` result, passed as `streakDays`.
 *
 * Empty / first-run (no plan ⇒ `days` is empty, `streakDays` 0): greyed
 * flame + "Start your streak", a hollow ring + "0 of 0 sessions", and no
 * on-track chip — honestly empty, no fake data (decision 6).
 *
 * The completion beat (PR 2 LoopHero) passes `celebrating` to pop the
 * streak count and fill+pop today's days-done segment.
 */
import type { Day } from '../../fixtures/plan';
import type { DistanceUnit } from '../../lib/actualForPlanDay';
import { DaysDoneRing } from './DaysDoneRing';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './RewardStrip.module.css';

export interface RewardStripProps {
  /** Current consecutive-completed-day streak (`computeCurrentStreak`). */
  streakDays: number;
  days: Day[];
  weekMileageDone: number;
  weekMileageTarget: number;
  /** Completion beat — pop the streak count + the filling days-done
   *  segment (direction §5). Reduced motion keeps state, drops springs. */
  celebrating?: boolean;
  /**
   * SP-8 B-4/B-5 (today_density_v1) decision D4 — mobile micro-trims so the
   * strip holds one line at 390pt: streak label drops "day" ("day streak" →
   * "streak"), the on-track chip drops "this week" ("N mi this week · on
   * track" → "N mi · on track"). Default false = byte-identical copy.
   */
  dense?: boolean;
  /**
   * B332 / B314 — the unit `weekMileageDone` is stated in (the athlete's display
   * unit). Default 'mi' = byte-identical copy for a mile athlete.
   */
  unit?: DistanceUnit;
  /**
   * B330 — whether the week is on plan (`WeekDistanceSummary.weekOnTrack`). False
   * hides the line. Omitted (a caller that has no plan to compare, such as the
   * dev icon sheet) leaves the line to the two guards below.
   */
  onTrack?: boolean;
}

export function RewardStrip({
  streakDays,
  days,
  weekMileageDone,
  weekMileageTarget,
  celebrating,
  dense,
  unit = 'mi',
  onTrack,
}: RewardStripProps) {
  const iconsOn = useIconSystem();
  const hasStreak = streakDays > 0;
  // On-track chip needs a real weekly target (first-run has none) AND something
  // done: "0 mi · on track" on the last day of a week with nothing run told a
  // teen who had done nothing that they were fine (B330). `onTrack` is the
  // plan comparison (done reaches the days already passed); false hides it.
  const showOnTrack = weekMileageTarget > 0 && weekMileageDone > 0 && onTrack !== false;

  return (
    <div className={styles.reward}>
      {/* 1 · Streak */}
      <div className={styles.group}>
        <span
          className={[styles.flame, hasStreak ? '' : styles.flameMuted].filter(Boolean).join(' ')}
          aria-hidden="true"
        >
          {/* CD-ICONS-01 — `chip.streak` @16, the same flame the StreakChip
              uses (it aliases `kudo.fire`; one flame family). The muted
              zero-state is a CLASS on this span, so the glyph keeps inheriting
              its colour and needs no variant. Flag OFF = the untouched 🔥. */}
          {iconsOn ? <Icon name="chip.streak" size={16} /> : '🔥'}
        </span>
        {hasStreak ? (
          <>
            <span
              className={[styles.streakNum, celebrating ? styles.pop : '']
                .filter(Boolean)
                .join(' ')}
            >
              {streakDays}
            </span>
            <span className={styles.lbl}>{dense ? 'streak' : 'day streak'}</span>
          </>
        ) : (
          <span className={styles.lbl}>Start your streak</span>
        )}
      </div>

      <span className={styles.sep} aria-hidden="true" />

      {/* 2 · Days-done ring (W3) */}
      <DaysDoneRing days={days} />

      {/* 3 · On-track / week mileage */}
      {showOnTrack && (
        <>
          <span className={styles.sep} aria-hidden="true" />
          <div className={styles.onTrack}>
            {/* CD-ICONS-01 — `sys.check` @16 ("forms · done states").
                ⚠️ DELIBERATELY NOT `chip.reward`. The migration map names
                `chip.reward` for this FILE, but the strip's two emoji are a
                streak flame and an on-track tick — neither is an earned token,
                and `chip.reward`'s solid star means "earned" in the fill
                vocabulary. Painting "on track" as "earned" would be an art
                decision this lane has no mandate to make, so the tick takes the
                tick glyph and `chip.reward`'s mount is an open question for the
                founder. Flag OFF = the untouched ✓. */}
            <span className={styles.check} aria-hidden="true">
              {iconsOn ? <Icon name="sys.check" size={16} /> : '✓'}
            </span>
            {weekMileageDone} {unit}{dense ? '' : ' this week'} · on track
          </div>
        </>
      )}
    </div>
  );
}
